/**
 * tableErrors.ts
 *
 * Shared classifier for "this table doesn't exist any more" vs "the load
 * just failed" (#969 Part E — Broken table reference).
 *
 * useTableData (hooks/useTableData.ts) unwraps every load failure into a
 * plain `Error` carrying only `extractErrorMessage(error)` — the original
 * axios error (and its HTTP status) does not survive. So the only signal
 * available here is the message text, which for a missing blob is GoAPI's
 * `loadCBTFile` error — "failed to load CBT file: <blob store error>" — and
 * for Azure specifically that blob-store error says the blob "does not
 * exist" or is "not found". A transient failure (500, timeout, offline) uses
 * different wording and must NOT be flagged as "table missing": that would
 * make a network blip permanently blank a widget's reference.
 */

const NOT_FOUND_PATTERN = /not found|does not exist|no such (?:table|blob|file)/i

// Wording used by generic/transient failures this must never match, even
// though some contain digits or words that could look table-shaped.
const TRANSIENT_PATTERN = /network error|timed?\s?out|timeout|unreachable|bad gateway|gateway timeout|service unavailable|ssl|unexpected response|econnrefused|econnreset/i

/**
 * True when an error (already reduced to a message string, as
 * useTableData/extractErrorMessage produce) means "the table is gone" —
 * renamed, deleted, or moved — as opposed to a transient load failure.
 */
export function isTableNotFoundMessage(message: string | null | undefined): boolean {
  if (!message) return false
  if (TRANSIENT_PATTERN.test(message)) return false
  return NOT_FOUND_PATTERN.test(message)
}

/**
 * Same classification, taking the `error` value a caller has in hand
 * (useTableData's `error` field, or any thrown Error/unknown value).
 */
export function isTableNotFoundError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  return isTableNotFoundMessage(message)
}
